import React, { useEffect, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { MapPin, Loader2, Radio, Square } from "lucide-react";
import { toast } from "sonner";

/**
 * LiveGpsCard for a single booking.
 * - Sitter view: shows "Start sharing" / "Stop sharing" toggle; on start,
 *   the browser geolocates every 20s and POSTs to /bookings/{id}/location.
 * - Family view: polls the latest sitter location every 15s.
 */
export default function LiveGpsCard({ booking, myRole }) {
  const isSitter = myRole === "sitter";
  const [loc, setLoc] = useState(null);
  const [sharing, setSharing] = useState(false);
  const timerRef = useRef(null);
  const watchRef = useRef(null);

  const canShare = ["accepted", "paid"].includes(booking.status);

  const fetchLatest = async () => {
    try {
      const { data } = await api.get(`/bookings/${booking.id}/location`);
      if (data && data.lat != null) setLoc(data);
    } catch (e) { console.warn("[LiveGpsCard] location fetch failed", e); }
  };

  useEffect(() => {
    if (!canShare) return;
    fetchLatest();
    const iv = setInterval(fetchLatest, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, booking.status]);

  const startSharing = () => {
    if (!("geolocation" in navigator)) return toast.error("Geolocation not supported");
    setSharing(true);
    const push = (pos) => {
      const payload = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
      };
      api.post(`/bookings/${booking.id}/location`, payload)
        .then((r) => setLoc(r.data))
        .catch((e) => toast.error(extractError(e)));
    };
    const err = (e) => { toast.error("Location error: " + e.message); stopSharing(); };
    watchRef.current = navigator.geolocation.watchPosition(push, err, { enableHighAccuracy: true, maximumAge: 5000 });
    timerRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(push, err, { enableHighAccuracy: true });
    }, 20000);
  };

  const stopSharing = () => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    watchRef.current = null; timerRef.current = null;
    setSharing(false);
  };

  useEffect(() => () => stopSharing(), []);

  if (!canShare) return null;

  return (
    <div className="mt-3 rounded-lg border border-stone2-200 bg-stone2-50 p-3" data-testid={`booking-gps-${booking.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-sage-900">
          <MapPin className="w-4 h-4 text-sage-600" /> Live location
        </div>
        {isSitter && (
          sharing ? (
            <button type="button" onClick={stopSharing} data-testid={`gps-stop-${booking.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-danger text-white">
              <Square className="w-3 h-3" /> Stop sharing
            </button>
          ) : (
            <button type="button" onClick={startSharing} data-testid={`gps-start-${booking.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1 bg-sage-600 text-white">
              <Radio className="w-3 h-3" /> Start sharing
            </button>
          )
        )}
      </div>
      {loc?.lat != null ? (
        <div className="mt-2 text-xs text-stone2-700">
          <div>Lat {loc.lat.toFixed(5)}, Lng {loc.lng.toFixed(5)}
            {loc.accuracy_m ? ` · ±${Math.round(loc.accuracy_m)}m` : ""}</div>
          <div className="text-stone2-500">Updated {loc.updated_at ? new Date(loc.updated_at).toLocaleTimeString() : "—"}</div>
          <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer"
            data-testid={`gps-open-map-${booking.id}`}
            className="mt-1 inline-block text-sage-700 hover:text-sage-900 font-semibold">Open in Maps →</a>
        </div>
      ) : (
        <div className="mt-2 text-xs text-stone2-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          {isSitter ? "Not sharing yet." : "Sitter has not started sharing."}
        </div>
      )}
    </div>
  );
}
